import { useState } from "react"
import InputMask from "react-input-mask"
import { Chip } from "@/components/Molecules/Chip/Chip"
import { Icon } from "@/components/Atoms/Icon"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"

export interface EventsFilterProps {
  search: string
  onSearchChange: (value: string) => void
  inscriptionFilter: string
  onInscriptionFilterChange: (value: string) => void
  typeFilter: string
  onTypeFilterChange: (value: string) => void
  periodFilter: string
  onPeriodFilterChange: (value: string) => void
  startDate: Date | null
  onStartDateChange: (date: Date | null) => void
  endDate: Date | null
  onEndDateChange: (date: Date | null) => void
}

const INSCRIPTION_LABELS: Record<string, string> = {
  inscrito: "Inscrito",
  disponivel: "Disponível",
}

const TYPE_LABELS: Record<string, string> = {
  evento: "Eventos",
  imersao: "Imersões",
}

const PERIOD_LABELS: Record<string, string> = {
  "este-mes": "Este mês",
  "proximo-mes": "Próximo mês",
  "proximos-3-meses": "Próximos 3 meses",
  "este-ano": "Este ano",
}

function parseBRDate(value: string): Date | null {
  const digits = value.replace(/\D/g, "")
  if (digits.length !== 8) return null
  const day = parseInt(digits.slice(0, 2))
  const month = parseInt(digits.slice(2, 4)) - 1
  const year = parseInt(digits.slice(4, 8))
  const date = new Date(year, month, day)
  return isNaN(date.getTime()) ? null : date
}

export const EventsFilter: React.FC<EventsFilterProps> = ({
  search,
  onSearchChange,
  inscriptionFilter,
  onInscriptionFilterChange,
  typeFilter,
  onTypeFilterChange,
  periodFilter,
  onPeriodFilterChange,
  onStartDateChange,
  onEndDateChange,
}) => {
  // Desktop custom date state
  const [startText, setStartText] = useState("")
  const [endText, setEndText] = useState("")
  const [committedStart, setCommittedStart] = useState("")
  const [committedEnd, setCommittedEnd] = useState("")

  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draftInscription, setDraftInscription] = useState("todos")
  const [draftType, setDraftType] = useState("todos")
  const [draftPeriod, setDraftPeriod] = useState("todos")
  const [draftStartText, setDraftStartText] = useState("")
  const [draftEndText, setDraftEndText] = useState("")

  const hasActiveFilters =
    inscriptionFilter !== "todos" || typeFilter !== "todos" || periodFilter !== "todos"

  const openDrawer = () => {
    setDraftInscription(inscriptionFilter)
    setDraftType(typeFilter)
    setDraftPeriod(periodFilter)
    setDraftStartText(committedStart)
    setDraftEndText(committedEnd)
    setDrawerOpen(true)
  }

  const applyFilters = () => {
    onInscriptionFilterChange(draftInscription)
    onTypeFilterChange(draftType)
    onPeriodFilterChange(draftPeriod)
    if (draftPeriod === "customizado") {
      const s = parseBRDate(draftStartText)
      const e = parseBRDate(draftEndText)
      onStartDateChange(s)
      onEndDateChange(e)
      setCommittedStart(s ? draftStartText : "")
      setCommittedEnd(e ? draftEndText : "")
      setStartText(draftStartText)
      setEndText(draftEndText)
    } else {
      onStartDateChange(null)
      onEndDateChange(null)
      setCommittedStart("")
      setCommittedEnd("")
    }
    setDrawerOpen(false)
  }

  const clearFilters = () => {
    setDraftInscription("todos")
    setDraftType("todos")
    setDraftPeriod("todos")
    setDraftStartText("")
    setDraftEndText("")
    onInscriptionFilterChange("todos")
    onTypeFilterChange("todos")
    onPeriodFilterChange("todos")
    onStartDateChange(null)
    onEndDateChange(null)
    setCommittedStart("")
    setCommittedEnd("")
    setStartText("")
    setEndText("")
    setDrawerOpen(false)
  }

  // Desktop blur handlers
  const handleStartBlur = () => {
    const date = parseBRDate(startText)
    if (date) {
      setCommittedStart(startText)
      onStartDateChange(date)
    } else {
      setCommittedStart("")
      onStartDateChange(null)
    }
  }

  const handleEndBlur = () => {
    const date = parseBRDate(endText)
    if (date) {
      setCommittedEnd(endText)
      onEndDateChange(date)
    } else {
      setCommittedEnd("")
      onEndDateChange(null)
    }
  }

  const handleRemovePeriod = () => {
    onPeriodFilterChange("todos")
    onStartDateChange(null)
    onEndDateChange(null)
    setStartText("")
    setEndText("")
    setCommittedStart("")
    setCommittedEnd("")
  }

  const activeChips: { key: string; label: string; onRemove: () => void }[] = []

  if (inscriptionFilter !== "todos") {
    activeChips.push({
      key: "inscription",
      label: INSCRIPTION_LABELS[inscriptionFilter] ?? inscriptionFilter,
      onRemove: () => onInscriptionFilterChange("todos"),
    })
  }

  if (typeFilter !== "todos") {
    activeChips.push({
      key: "type",
      label: TYPE_LABELS[typeFilter] ?? typeFilter,
      onRemove: () => onTypeFilterChange("todos"),
    })
  }

  if (periodFilter !== "todos") {
    if (periodFilter === "customizado") {
      if (committedStart || committedEnd) {
        const parts = []
        if (committedStart) parts.push(`De: ${committedStart}`)
        if (committedEnd) parts.push(`Até: ${committedEnd}`)
        activeChips.push({
          key: "period",
          label: parts.join(" "),
          onRemove: handleRemovePeriod,
        })
      }
    } else {
      activeChips.push({
        key: "period",
        label: PERIOD_LABELS[periodFilter] ?? periodFilter,
        onRemove: handleRemovePeriod,
      })
    }
  }

  return (
    <div>
      {/* Mobile layout */}
      <div className="lg:hidden">
        <div className="flex items-center gap-3">
          <div className="relative flex flex-1 items-center gap-3 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar evento..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2.5 focus:outline-none"
            />
          </div>
          <button
            onClick={openDrawer}
            className="relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-black/40 transition-colors"
          >
            <i
              className={`fi fi-ts-sliders-v flex items-center justify-center text-base ${hasActiveFilters ? "text-orange-500" : "text-white"}`}
            />
            {hasActiveFilters && (
              <span className="absolute -right-1 -top-1 flex size-3 items-center justify-center rounded-full bg-orange-500" />
            )}
          </button>
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:block">
        <div className="flex items-center gap-4">
          <div className="relative flex w-80 items-center gap-4 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar evento..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2 focus:outline-none"
            />
          </div>

          <Select onValueChange={onInscriptionFilterChange} value={inscriptionFilter}>
            <SelectTrigger
              className={`w-40 border-border bg-surface ${inscriptionFilter !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todos os Status" />
            </SelectTrigger>
            <SelectContent className="border-border bg-surface">
              <SelectItem value="todos">Todos os Status</SelectItem>
              <SelectItem value="inscrito">Inscrito</SelectItem>
              <SelectItem value="disponivel">Disponível</SelectItem>
            </SelectContent>
          </Select>

          <Select onValueChange={onTypeFilterChange} value={typeFilter}>
            <SelectTrigger
              className={`w-40 border-border bg-surface ${typeFilter !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todos os Tipos" />
            </SelectTrigger>
            <SelectContent className="border-border bg-surface">
              <SelectItem value="todos">Todos os Tipos</SelectItem>
              <SelectItem value="evento">Eventos</SelectItem>
              <SelectItem value="imersao">Imersões</SelectItem>
            </SelectContent>
          </Select>

          {periodFilter !== "customizado" ? (
            <Select onValueChange={onPeriodFilterChange} value={periodFilter}>
              <SelectTrigger
                className={`w-44 border-border bg-surface ${periodFilter !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
              >
                <SelectValue placeholder="Qualquer data" />
              </SelectTrigger>
              <SelectContent className="border-border bg-surface">
                <SelectItem value="todos">Qualquer data</SelectItem>
                <SelectItem value="este-mes">Este mês</SelectItem>
                <SelectItem value="proximo-mes">Próximo mês</SelectItem>
                <SelectItem value="proximos-3-meses">Próximos 3 meses</SelectItem>
                <SelectItem value="este-ano">Este ano</SelectItem>
                <SelectItem value="customizado">Customizado</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-orange-500 bg-orange-500/10 px-3 py-2">
              <InputMask
                mask="99/99/9999"
                value={startText}
                onChange={(e) => setStartText(e.target.value)}
                onBlur={handleStartBlur}
              >
                {(inputProps: any) => (
                  <input
                    {...inputProps}
                    placeholder="dd/mm/yyyy"
                    className="w-24 bg-transparent text-sm text-orange-400 placeholder-orange-500/40 focus:outline-none"
                  />
                )}
              </InputMask>
              <i className="fi fi-ts-arrow-right flex items-center text-sm text-orange-500/60" />
              <InputMask
                mask="99/99/9999"
                value={endText}
                onChange={(e) => setEndText(e.target.value)}
                onBlur={handleEndBlur}
              >
                {(inputProps: any) => (
                  <input
                    {...inputProps}
                    placeholder="dd/mm/yyyy"
                    className="w-24 bg-transparent text-sm text-orange-400 placeholder-orange-500/40 focus:outline-none"
                  />
                )}
              </InputMask>
              <button
                onClick={handleRemovePeriod}
                className="ml-1 flex items-center text-orange-500/60 transition-colors hover:text-orange-500"
              >
                <i className="fi fi-rs-x flex items-center text-[10px]" />
              </button>
            </div>
          )}

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-orange-500 whitespace-nowrap"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="border-white/10 bg-surface px-4 pb-2">
          <DrawerHeader>
            <DrawerTitle className="text-white">Filtros</DrawerTitle>
          </DrawerHeader>

          <div className="flex flex-col gap-4 px-4 pb-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Status</span>
              <Select onValueChange={setDraftInscription} value={draftInscription}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftInscription !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todos os Status" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="todos">Todos os Status</SelectItem>
                  <SelectItem value="inscrito">Inscrito</SelectItem>
                  <SelectItem value="disponivel">Disponível</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Tipo</span>
              <Select onValueChange={setDraftType} value={draftType}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftType !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todos os Tipos" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="todos">Todos os Tipos</SelectItem>
                  <SelectItem value="evento">Eventos</SelectItem>
                  <SelectItem value="imersao">Imersões</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Período</span>
              <Select onValueChange={setDraftPeriod} value={draftPeriod}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftPeriod !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Qualquer data" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="todos">Qualquer data</SelectItem>
                  <SelectItem value="este-mes">Este mês</SelectItem>
                  <SelectItem value="proximo-mes">Próximo mês</SelectItem>
                  <SelectItem value="proximos-3-meses">Próximos 3 meses</SelectItem>
                  <SelectItem value="este-ano">Este ano</SelectItem>
                  <SelectItem value="customizado">Customizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {draftPeriod === "customizado" && (
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Intervalo</span>
                <div className="flex items-center gap-2 rounded-lg border border-orange-500 bg-orange-500/10 px-4 py-2">
                  <InputMask
                    mask="99/99/9999"
                    value={draftStartText}
                    onChange={(e) => setDraftStartText(e.target.value)}
                  >
                    {(inputProps: any) => (
                      <input
                        {...inputProps}
                        placeholder="dd/mm/yyyy"
                        className="flex-1 bg-transparent text-sm text-orange-400 placeholder-orange-500/40 focus:outline-none"
                      />
                    )}
                  </InputMask>
                  <i className="fi fi-ts-arrow-right flex items-center text-sm text-orange-500/60" />
                  <InputMask
                    mask="99/99/9999"
                    value={draftEndText}
                    onChange={(e) => setDraftEndText(e.target.value)}
                  >
                    {(inputProps: any) => (
                      <input
                        {...inputProps}
                        placeholder="dd/mm/yyyy"
                        className="flex-1 bg-transparent text-sm text-orange-400 placeholder-orange-500/40 focus:outline-none"
                      />
                    )}
                  </InputMask>
                </div>
              </div>
            )}
          </div>

          <DrawerFooter>
            <Button
              onClick={applyFilters}
              className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600"
            >
              Aplicar filtro
            </Button>
            <Button
              onClick={clearFilters}
              variant="ghost"
              className="w-full text-muted-foreground hover:text-white"
            >
              Limpar filtro
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
